/* Why Solved — feature detail modal (bottom sheet on mobile, centered dialog on desktop) */

const SWIPE_CLOSE_THRESHOLD = 70;
const CLOSE_TRANSITION_FALLBACK_MS = 450;

class WhySolvedModal extends HTMLElement {
  connectedCallback() {
    this.panel = this.querySelector('[data-modal-panel]');
    this.overlay = this.querySelector('[data-modal-overlay]');
    this.lastTrigger = null;
    this.touchStartY = null;
    this.touchDeltaY = 0;

    this.handleTriggerClick = this.handleTriggerClick.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchMove = this.handleTouchMove.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);

    document.addEventListener('click', this.handleTriggerClick);
    this.addEventListener('click', this.handleClick);
    this.panel?.addEventListener('touchstart', this.handleTouchStart, { passive: true });
    this.panel?.addEventListener('touchmove', this.handleTouchMove, { passive: true });
    this.panel?.addEventListener('touchend', this.handleTouchEnd);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.handleTriggerClick);
    document.removeEventListener('keydown', this.handleKeydown);
    document.documentElement.classList.remove('why-solved-modal-lock');
  }

  handleTriggerClick(event) {
    const trigger = event.target.closest(`[aria-controls="${this.id}"]`);
    if (!trigger) return;
    event.preventDefault();
    this.open(trigger);
  }

  handleClick(event) {
    if (event.target === this.overlay || event.target.closest('[data-modal-close]')) {
      this.close();
    }
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      this.close();
      return;
    }
    if (event.key !== 'Tab') return;

    const focusables = [
      ...this.panel.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'),
    ];
    if (!focusables.length) return;

    const first = focusables[0];
    const last = focusables[focusables.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  handleTouchStart(event) {
    // Only start a dismiss gesture when the panel is scrolled to the top,
    // so swiping down to read content doesn't close the modal.
    if (this.panel.scrollTop > 0) return;
    this.touchStartY = event.touches[0].clientY;
    this.touchDeltaY = 0;
  }

  handleTouchMove(event) {
    if (this.touchStartY === null) return;
    this.touchDeltaY = event.touches[0].clientY - this.touchStartY;
  }

  handleTouchEnd() {
    if (this.touchStartY !== null && this.touchDeltaY > SWIPE_CLOSE_THRESHOLD) {
      this.close();
    }
    this.touchStartY = null;
    this.touchDeltaY = 0;
  }

  open(trigger) {
    this.lastTrigger = trigger;
    this.hidden = false;
    // Force a reflow so the opening transition runs from the closed state.
    this.panel.getBoundingClientRect();
    this.classList.add('is-open');
    document.documentElement.classList.add('why-solved-modal-lock');
    document.addEventListener('keydown', this.handleKeydown);
    trigger.setAttribute('aria-expanded', 'true');
    this.panel.focus();

    document.dispatchEvent(
      new CustomEvent('theme:modal:opened', { detail: { id: this.id }, bubbles: true })
    );
  }

  close() {
    if (!this.classList.contains('is-open')) return;

    this.classList.remove('is-open');
    document.documentElement.classList.remove('why-solved-modal-lock');
    document.removeEventListener('keydown', this.handleKeydown);
    this.lastTrigger?.setAttribute('aria-expanded', 'false');
    this.lastTrigger?.focus();

    const hideWhenDone = (event) => {
      if (event && event.target !== this.panel) return;
      this.panel.removeEventListener('transitionend', hideWhenDone);
      if (!this.classList.contains('is-open')) this.hidden = true;
    };
    this.panel.addEventListener('transitionend', hideWhenDone);
    // Fallback in case transitionend never fires (e.g. reduced motion).
    window.setTimeout(() => hideWhenDone({ target: this.panel }), CLOSE_TRANSITION_FALLBACK_MS);

    document.dispatchEvent(
      new CustomEvent('theme:modal:closed', { detail: { id: this.id }, bubbles: true })
    );
  }
}

if (!customElements.get('why-solved-modal')) {
  customElements.define('why-solved-modal', WhySolvedModal);
}
